import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LOAN_REQUEST_OFFER_SOURCE } from 'cpool-constants';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys.ts';

type Props = {
    source: LOAN_REQUEST_OFFER_SOURCE;
    id: string;
    leadId: string;
};

export const useOfferCommissionMutation = ({ id, leadId, source }: Props) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const queryKeyGetOffers = queryKeys.getAcceptedOffers(leadId);
    const queryKeyGetOfferCounts = queryKeys.getAcceptedOfferCounts(leadId);
    return useMutation({
        mutationFn: async (commission: SchemaLoanRequestResolver_GetAcceptedOffers[number]['provision']) => {
            return APICommunicator.sendCommand({
                aggregateId: leadId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.changeOfferCommission,
                payload: {
                    commission,
                    id,
                    source,
                },
            });
        },
        onError: (_error) => {
            toast.error(t('OFFER_MANAGEMENT.TOASTS.COMMISSION_CHANGED_FAILED'));
        },
        onMutate: async (commission: SchemaLoanRequestResolver_GetAcceptedOffers[number]['provision']) => {
            queryClient.setQueryData(queryKeyGetOffers, (data: SchemaLoanRequestResolver_GetAcceptedOffers) => {
                const mutatedData = structuredClone(data);
                const oldOffer = mutatedData?.find((offer) => offer.id === id);
                if (oldOffer === undefined) {
                    return data;
                }
                oldOffer.provision = commission;
                return mutatedData;
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: queryKeyGetOfferCounts });
            queryClient.invalidateQueries({ queryKey: queryKeyGetOffers });
        },
        onSuccess: ({ position }) => {
            setQueryPosition(JSON.stringify(queryKeyGetOfferCounts), position);
            setQueryPosition(JSON.stringify(queryKeyGetOffers), position);
        },
    });
};
